import { useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { TextField, Grid } from "@mui/material";
import { Info } from "@mui/icons-material";
import { providerProfileState } from "../../recoil/atoms";
import { GetProfile } from "../Profile/Services/UpdateProfileInfoService";
import { GetAllNewKitPickupLimsAccounts } from "./Services/NewKitPickupService";
import {
  Auth0UserSerializer,
  UiActionType,
  UserProviderProfile,
} from "../../recoil/types";
import {
  KitPickupErrorDetails,
  KitStepper,
  NewKitPickupClinicAccount,
  NewKitPickupDetails,
} from "./types";

import {
  KitPickupErrorMessage,
  KitPickupHelper,
  KitPickupStyledFormLabel,
  KitPickupTextField,
} from "./KitPickup.styles";
import FormField from "../shared/TextField/FormField";
import { WarningIcon } from "../../assets/iconComponents/Warning";
import { SendActionType } from "../shared/Services/SharedServices";

const KitPickupInformation: React.FC<{
  auth: Auth0UserSerializer | undefined;
  newKitPickupClinicAccount: NewKitPickupClinicAccount;
  setNewKitPickupClinicAccount: (account: NewKitPickupClinicAccount) => void;
  newKitPickupDetails: NewKitPickupDetails;
  setNewKitPickupDetails: (details: NewKitPickupDetails) => void;
  setLoggedOut: (inactivity: boolean) => void;
  errorDetails: KitPickupErrorDetails;
  setErrorDetails: (details: KitPickupErrorDetails) => void;
  resetErrorDetails: () => void;
  activeStep: KitStepper;
}> = ({
  auth,
  newKitPickupClinicAccount,
  setNewKitPickupClinicAccount,
  newKitPickupDetails,
  setNewKitPickupDetails,
  setLoggedOut,
  errorDetails,
  setErrorDetails,
  resetErrorDetails,
  activeStep,
}) => {
  const [clinicAccounts, setClinicAccounts] = useState<
    NewKitPickupClinicAccount[]
  >([]);

  const [profileInfo, setProfile] = useRecoilState(providerProfileState);

  useEffect(() => {
    const getAllClinicAccounts = async () => {
      if (auth && !clinicAccounts.length) {
        const allClinicAccounts = await GetAllNewKitPickupLimsAccounts(
          auth!.token,
          setLoggedOut,
        );
        setClinicAccounts(allClinicAccounts);
      }
    };
    getAllClinicAccounts();
  }, [auth]);

  useEffect(() => {
    const getFullProfileInfo = async () => {
      if (auth && !profileInfo && clinicAccounts.length) {
        const profileResponse = await GetProfile(auth.token, setLoggedOut);
        if (profileResponse?.ok && clinicAccounts.length) {
          const profileData =
            (await profileResponse.json()) as UserProviderProfile;
          setProfile(profileData);
          const initialClinic =
            clinicAccounts.find(
              (account) => account?.lims_id === profileData.account?.lims_id,
            ) || clinicAccounts[0];
          if (!newKitPickupClinicAccount.sfname) {
            setNewKitPickupClinicAccount(initialClinic);
          }
        }
      } else if (
        profileInfo &&
        !newKitPickupClinicAccount.sfname &&
        clinicAccounts.length
      ) {
        const initialClinic =
          clinicAccounts.find(
            (account) => account?.lims_id === profileInfo.account?.lims_id,
          ) || clinicAccounts[0];
        setNewKitPickupClinicAccount(initialClinic);
      }
    };
    // Set international account error if any clinic is not US-based
    if (
      clinicAccounts.some((clinic) => clinic.billingcountrycode !== "US") ||
      profileInfo?.profile.is_demo_user
    ) {
      setErrorDetails({ ...errorDetails, isInternationalAccountError: true });
    }
    getFullProfileInfo();
  }, [clinicAccounts]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    resetErrorDetails();
    setNewKitPickupDetails({
      ...(newKitPickupDetails || {}),
      [e.target.name]: e.target.value,
    } as NewKitPickupDetails);
  };

  const getReadyDateHelperText = () => {
    if (errorDetails.isReadyDateError) {
      return "Ready date must be either today or tomorrow. Contact BTO logistics to schedule pickups multiple days in advance.";
    }
    if (errorDetails.isReadyDateDayOfWeekError) {
      return "Ready date must be a business day.";
    }
    if (errorDetails.isSameDayPickupError) {
      return "Same day pickups must be scheduled before Noon.";
    }
    return "";
  };

  const getReadyTimeHelperText = () => {
    if (errorDetails.isEarlyReadyTimeError) {
      return "Ready time must be later than the current time for same day pickups.";
    }
    if (errorDetails.isCustomerCloseTimeError) {
      return "Ready time must be at least 3 hours before customer close time.";
    }
    return "";
  };

  const getErrorMessageText = () => {
    if (errorDetails.isAPIError) {
      return (
        errorDetails.apiErrorMessage ||
        "Something went wrong! Try submitting your request again.\nIf your issue persists, reach out to BTO logistics at (217) 807-4707 or logistics@billiontoone.com"
      );
    }
    if (errorDetails.isInternationalAccountError) {
      return "Kit Pickup is not currently available outside the US.";
    }
    return "Please make sure all fields are filled out correctly, then click confirm.";
  };

  const sendActionType = async (actionType: UiActionType) => {
    await SendActionType(actionType, setLoggedOut, auth?.token);
  };

  return (
    <Grid container spacing={3}>
      {(activeStep === KitStepper.Confirmation ||
        errorDetails.isInternationalAccountError) && (
        <Grid item xs={12} sx={{ mt: 2, mb: -2, px: 2 }}>
          <KitPickupErrorMessage
            $isError={
              errorDetails.isAPIError ||
              errorDetails.isInternationalAccountError
            }
          >
            {(errorDetails.isAPIError ||
              errorDetails.isInternationalAccountError) && (
              <WarningIcon testId="WarningIcon" />
            )}
            {getErrorMessageText()}
          </KitPickupErrorMessage>
        </Grid>
      )}
      <Grid item xs={12} sx={{ pt: 0 }}>
        <KitPickupStyledFormLabel sx={{ mb: 0, display: "block" }}>
          Pickup Time
        </KitPickupStyledFormLabel>
      </Grid>
      <Grid item xs={6}>
        <KitPickupTextField
          variant="standard"
          label="Ready Date"
          type="date"
          name="ready_date"
          value={newKitPickupDetails?.ready_date || ""}
          InputLabelProps={{ shrink: true }}
          fullWidth
          error={
            (!newKitPickupDetails?.ready_date &&
              errorDetails.isEmptyFieldsError) ||
            getReadyDateHelperText() !== ""
          }
          onChange={onChange}
          helperText={getReadyDateHelperText()}
          disabled={activeStep === KitStepper.Confirmation}
        />
      </Grid>
      <Grid item xs={6}>
        <KitPickupTextField
          variant="standard"
          label="Pickup Ready Time"
          type="time"
          name="ready_time"
          value={newKitPickupDetails?.ready_time || ""}
          InputLabelProps={{ shrink: true }}
          fullWidth
          error={
            (!newKitPickupDetails?.ready_time &&
              errorDetails.isEmptyFieldsError) ||
            getReadyTimeHelperText() !== ""
          }
          onChange={onChange}
          helperText={getReadyTimeHelperText()}
          disabled={activeStep === KitStepper.Confirmation}
        />
        {getReadyTimeHelperText() === "" && (
          <KitPickupHelper>
            {activeStep === KitStepper.Order && <Info />}
            {activeStep === KitStepper.Order
              ? "Pickup can occur at any time between ready time and close time."
              : ""}
          </KitPickupHelper>
        )}
      </Grid>
      <Grid item xs={6}>
        <KitPickupTextField
          variant="standard"
          label="Pickup Location Close Time"
          type="time"
          name="customer_close_time"
          value={newKitPickupDetails?.customer_close_time || ""}
          InputLabelProps={{ shrink: true }}
          fullWidth
          error={
            (!newKitPickupDetails?.customer_close_time &&
              errorDetails.isEmptyFieldsError) ||
            getReadyTimeHelperText() !== ""
          }
          onChange={onChange}
          disabled={activeStep === KitStepper.Confirmation}
        />
      </Grid>
      <Grid item xs={6}>
        <KitPickupTextField
          variant="standard"
          label="Pickup Contact Name"
          type="text"
          name="person_name"
          value={newKitPickupDetails?.person_name || ""}
          InputLabelProps={{ shrink: true }}
          fullWidth
          error={
            !newKitPickupDetails?.person_name && errorDetails.isEmptyFieldsError
          }
          onChange={onChange}
          disabled={activeStep === KitStepper.Confirmation}
        />
      </Grid>
      <Grid item xs={12} sx={{ pt: 1 }}>
        <KitPickupErrorMessage $isError={false}>
          <Info sx={{ mr: 1, fontSize: 20, color: "#064682" }} />
          Please consider the site&apos;s business hours and any lunch-time
          closures to ensure successful collection.
        </KitPickupErrorMessage>
      </Grid>
      <Grid item xs={12} sx={{ pt: 0, mt: "-10px" }}>
        <KitPickupStyledFormLabel sx={{ mb: 0, mt: 0, display: "block" }}>
          Pickup Address
        </KitPickupStyledFormLabel>
      </Grid>
      <Grid item xs={12}>
        <FormField
          fieldTitle="Clinic Name"
          fieldType="autocomplete"
          autoCompleteDropdownStyle
          autoCompleteOptions={clinicAccounts.map((clinic) => clinic.sfname)}
          fieldValue={newKitPickupClinicAccount?.sfname ?? ""}
          onFieldChange={(val) => {
            if (profileInfo) {
              const selectedClinic = clinicAccounts.find(
                (clinic) => clinic.sfname === val,
              );
              if (selectedClinic) {
                setNewKitPickupClinicAccount(selectedClinic);
                sendActionType("ProviderKitPickupSelectAccount");
              }
            }
          }}
          edit={activeStep !== KitStepper.Confirmation}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="standard"
          label="Street Address"
          type="text"
          value={newKitPickupClinicAccount?.billingstreet}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={5}>
        <TextField
          variant="standard"
          label="City"
          type="text"
          value={newKitPickupClinicAccount?.billingcity}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          variant="standard"
          label="State"
          type="text"
          value={newKitPickupClinicAccount?.billingstatecode}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          variant="standard"
          label="Zip Code"
          type="text"
          value={newKitPickupClinicAccount?.billingpostalcode}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          variant="standard"
          label="Country"
          type="text"
          value={newKitPickupClinicAccount?.billingcountrycode}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
    </Grid>
  );
};

export default KitPickupInformation;
